/**
 * Web Device Fingerprint — KI-045 Wave B10 d6.
 *
 * Browser-side counterpart to apps/mobile/src/services/device-fingerprint.ts.
 * Returns a stable, per-browser-profile UUID v4 that identifies this device
 * to the API. Persisted in IndexedDB so it survives normal page reloads and
 * tab closes, and rotates when the user clears site data (intentional —
 * matches the mobile model where a wipe gives the device a fresh identity).
 *
 * NOT a hardware fingerprint. We don't use canvas/WebGL/AudioContext tricks.
 * Apple's ITP and Chrome's Privacy Sandbox both flag those, and they violate
 * the spirit of "the user can reset their device identity by clearing cache"
 * which is the legal baseline for a child-account product.
 *
 * Fail-soft policy: if IndexedDB is unavailable (private browsing on Safari,
 * some embedded WebViews, storage quota exceeded), an in-memory UUID is
 * returned and persistence is skipped silently. Subsequent calls in the same
 * page session return the same in-memory value via the module-scoped cache,
 * but a new browser tab on a fail-soft browser will produce a fresh ID. The
 * device-registration server treats each new ID as a new device, which is
 * the conservative outcome (more revoke surfaces, not fewer).
 */

const DB_NAME = 'kantera-device';
const STORE_NAME = 'fingerprint';
const KEY = 'v1';
const DB_VERSION = 1;

let cached: string | null = null;

/**
 * Generate a UUID v4. Prefers `crypto.randomUUID()` (universal in modern
 * browsers since 2022) and falls back to `crypto.getRandomValues` with
 * manual byte twiddling for older runtimes.
 */
function uuidv4(): string {
  const g = globalThis as {
    crypto?: {
      randomUUID?: () => string;
      getRandomValues?: (a: Uint8Array) => Uint8Array;
    };
  };
  if (g.crypto?.randomUUID) {
    return g.crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (g.crypto?.getRandomValues) {
    g.crypto.getRandomValues(bytes);
  } else {
    // Last-resort fallback. The fingerprint identifies the device, not
    // authorises it — Math.random is acceptable here.
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8]! & 0x3f) | 0x80; // variant 10xx
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

function dbGet(db: IDBDatabase): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(KEY);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB get failed'));
    req.onsuccess = () => {
      const value = req.result;
      resolve(typeof value === 'string' ? value : null);
    };
  });
}

function dbPut(db: IDBDatabase, value: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.put(value, KEY);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB put failed'));
    req.onsuccess = () => resolve();
  });
}

/**
 * Returns the stable device fingerprint for this browser profile, creating
 * one on first call. Idempotent; safe to call from multiple components.
 */
export async function getOrCreateClientFingerprint(): Promise<string> {
  if (cached) return cached;

  // If IndexedDB isn't available (very old WebView, deliberate disable), or
  // if any step throws, fall through to in-memory.
  if (typeof indexedDB === 'undefined') {
    cached = uuidv4();
    return cached;
  }

  try {
    const db = await openDb();
    try {
      const existing = await dbGet(db);
      if (existing) {
        cached = existing;
        return existing;
      }
      const fresh = uuidv4();
      await dbPut(db, fresh);
      cached = fresh;
      return fresh;
    } finally {
      db.close();
    }
  } catch {
    cached = uuidv4();
    return cached;
  }
}

/**
 * Test-only hook to clear the module-scoped cache between cases. Not
 * exported from the public surface — tests import via the source path.
 */
export function __resetCacheForTests(): void {
  cached = null;
}
